import { DateTime } from "luxon";

// Bold sign limits due date to 180 days in the future
// use 178 days to be safe and avoid time zone issues
export const BOLD_SIGN_SAFE_FUTURE_DUE_DATE_DAYS = 178;

export interface ReviewerForValidation {
  order: number;
  dueDate?: DateTime | string | Date | null;
  isSigner?: boolean;
}

export interface ReviewerDueDateValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates reviewer due dates against submission rules:
 * 1. If reviewers are ordered (sequential), due dates (when set) must be in order (or equal)
 * 2. Reviewer due dates (when set) must be before (or equal to) the submission due date
 */
export function validateReviewerDueDates(
  reviewers: ReviewerForValidation[],
  submissionDueDate: DateTime | string | Date | null | undefined,
  isSequential: boolean
): ReviewerDueDateValidationResult {
  const errors: string[] = [];

  if (reviewers.length === 0) {
    return { isValid: true, errors: [] };
  }

  // Convert submission due date to DateTime if needed
  const submissionDueDateDT =
    typeof submissionDueDate === "string"
      ? DateTime.fromISO(submissionDueDate)
      : submissionDueDate instanceof Date
      ? DateTime.fromJSDate(submissionDueDate)
      : submissionDueDate;

  // Check each reviewer's due date
  for (const reviewer of reviewers) {
    if (!reviewer.dueDate) {
      continue; // Reviewer due dates are optional
    }

    const reviewerDueDateDT =
      typeof reviewer.dueDate === "string"
        ? DateTime.fromISO(reviewer.dueDate)
        : reviewer.dueDate instanceof Date
        ? DateTime.fromJSDate(reviewer.dueDate)
        : reviewer.dueDate;

    // Check reviewer due date is before or equal to submission due date (if submission due date is set)
    if (submissionDueDateDT && reviewerDueDateDT > submissionDueDateDT) {
      if (reviewer.isSigner) {
        errors.push(
          "Signer due date cannot be after the submission due date. "
        );
      } else {
        errors.push(
          `Reviewer with order ${reviewer.order} has a due date after the submission due date. `
        );
      }
    }

    // For signers, check if due date is within Bold Sign's limit
    // Bold Sign limits due date to 180 days in the future but we use 178 days to be safe and avoid time zone issues
    if (reviewer.isSigner) {
      const now = DateTime.now().startOf("day");
      const minFutureDate = now.plus({ days: 1 });
      const maxFutureDate = now.plus({
        days: BOLD_SIGN_SAFE_FUTURE_DUE_DATE_DAYS,
      });

      const reviewerDueDateStartOfDay = reviewerDueDateDT.startOf("day");

      if (reviewerDueDateStartOfDay < minFutureDate) {
        errors.push("Signer due date must be at least 1 day in the future. ");
      }

      if (reviewerDueDateStartOfDay > maxFutureDate) {
        errors.push(
          `Signer due date cannot be more than ${BOLD_SIGN_SAFE_FUTURE_DUE_DATE_DAYS} days in the future. `
        );
      }
    }
  }

  // If sequential, check that due dates are in order (including signers)
  if (isSequential && reviewers.length > 1) {
    // Sort by order to check chronologically
    const sortedReviewers = [...reviewers].sort((a, b) => a.order - b.order);

    for (let i = 0; i < sortedReviewers.length - 1; i++) {
      const current = sortedReviewers[i];
      const next = sortedReviewers[i + 1];

      if (!current.dueDate || !next.dueDate) {
        continue; // Skip if either is missing a due date (already reported above)
      }

      const currentDT =
        typeof current.dueDate === "string"
          ? DateTime.fromISO(current.dueDate)
          : current.dueDate instanceof Date
          ? DateTime.fromJSDate(current.dueDate)
          : current.dueDate;
      const nextDT =
        typeof next.dueDate === "string"
          ? DateTime.fromISO(next.dueDate)
          : next.dueDate instanceof Date
          ? DateTime.fromJSDate(next.dueDate)
          : next.dueDate;

      if (currentDT > nextDT) {
        errors.push(
          `Reviewer with order ${current.order} has a due date after reviewer with order ${next.order}. Sequential reviewers must have due dates in order. `
        );
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
